import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { deriveSubdomainSlug } from "../utils/subdomainFallback";
import OrgSwitcher from "./OrgSwitcher";

// SVG icon set matching the design
const Icons = {
  grid: (p: any) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  overview: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  editor: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M8 5v14l11-7Z"/></svg>
  ),
  settings: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.9 2.9l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.9-2.9l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.9-2.9l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.9 2.9l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"/>
    </svg>
  ),
  eye: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  logout: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
    </svg>
  ),
  chevDown: (p: any) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="m6 9 6 6 6-6"/>
    </svg>
  ),
};

interface TopbarProps {
  onGoToOverview?: () => void;
  onOpenSettings?: () => void;
}

export default function Topbar({ onGoToOverview, onOpenSettings }: TopbarProps): React.ReactElement {
  const { user, currentOrg, currentSubdomain, logout } = useAuth() as any;

  const orgName = currentOrg?.name || (user as any)?.org || "My Site";
  const userEmail = user?.email || "";
  const initials = (orgName as string).slice(0, 2).toUpperCase();

  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const orgMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!orgMenuOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (orgMenuRef.current && !orgMenuRef.current.contains(e.target as Node)) {
        setOrgMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [orgMenuOpen]);

  const handlePreviewSite = () => {
    const sub = deriveSubdomainSlug(user, currentSubdomain);
    if (sub) {
      window.open(`https://${sub}.users.godreamx.com`, "_blank");
    }
  };

  return (
    <div className="dx-topbar">
      {/* Brand */}
      <div className="dx-brand">
        <div className="dx-brand-mark">
          <Icons.grid />
        </div>
        <div className="dx-brand-meta">
          <div className="dx-brand-name">dreamx</div>
          <div className="dx-brand-sub">Web Builder</div>
        </div>
      </div>

      {/* Tab pills */}
      <div className="dx-tabs">
        <button className="dx-tab" onClick={onGoToOverview}>
          <Icons.overview /> Overview
        </button>
        <button className="dx-tab active">
          <Icons.editor /> Editor
        </button>
        <button className="dx-tab" onClick={onOpenSettings}>
          <Icons.settings /> Settings
        </button>
      </div>

      {/* Right actions */}
      <div className="dx-topbar-actions">
        <button className="dx-btn-ghost" onClick={handlePreviewSite}>
          <Icons.eye /> Preview site
        </button>

        <div className="dx-topbar-divider" />

        {/* Org switcher */}
        <div ref={orgMenuRef} style={{ position: "relative" }}>
          <button className="dx-org-btn" onClick={() => setOrgMenuOpen((v) => !v)}>
            <div className="dx-org-mark">{initials}</div>
            <div className="dx-org-meta">
              <div className="dx-org-name">{orgName}</div>
              <div className="dx-org-email">{userEmail}</div>
            </div>
            <Icons.chevDown className="dx-chev" />
          </button>
          {orgMenuOpen && <OrgSwitcher />}
        </div>

        <button className="dx-icon-btn" title="Sign out" onClick={logout}>
          <Icons.logout />
        </button>
      </div>
    </div>
  );
}
